import React, { useState, useRef } from 'react';
import DiscourseControls from '../DiscourseControls/DiscourseControls';
import styles from './Discourse.module.scss';
import { useAclContext } from '../../../Acl/Components/AclContextProvider';
import Text from '../../../../Components/Base/Text/Text';
import DiscourseHistory, {
	DiscourseHistoryRef,
} from '../DiscourseHistory/DiscourseHistory';
import { useDiscourseContext } from '../../Services/DiscourseContext';
import DiscourseCareClaimModal from '../DiscourseCareClaim/DiscourseCareClaimModal';
import useNotification from '../../../../Hooks/useNotification.hook';
import DiscourseRecord from '../DiscourseRecord/DiscourseRecord';

interface Props {
	originalId: string;
	patientId: string;
	workplaceId: string;
	caseId?: string;
}

const Discourse = ({ originalId, patientId, workplaceId, caseId }: Props) => {
	const { isAllowed } = useAclContext();
	const { handleEditDiscourseRecord } = useDiscourseContext();
	const [careClaimDiscourseId, setCareClaimDiscourseId] = useState<
		string | null
	>(null);
	const [showDiscourseForm, setShowDiscourseForm] = useState(false);
	const discourseHistoryRef = useRef<DiscourseHistoryRef>(null);
	const wrapperRef = useRef<HTMLDivElement>(null);
	const { showNotificationAlert } = useNotification();

	const scrollDiscourseToTop = () => {
		requestAnimationFrame(() => {
			wrapperRef.current?.scrollTo({
				top: 0,
				behavior: 'smooth',
			});
		});
	};

	const handleOpenCareClaimModal = (discourseId: string, diagnosis?: any) => {
		if (!diagnosis) {
			return showNotificationAlert({
				content: 'component.discourse.mainDiagnosisMissing',
			});
		}
		setCareClaimDiscourseId(discourseId);
	};

	const handleEditFromHistory = async (documentId: string) => {
		await handleEditDiscourseRecord(documentId);
		setShowDiscourseForm(true);
		scrollDiscourseToTop();
	};

	return (
		<div className={styles.wrapper} ref={wrapperRef}>
			<DiscourseControls
				allowElectronicPrescription={isAllowed('erecept_erecept')}
				allowMedicalReport={isAllowed('medical_report')}
				workplaceId={workplaceId}
				caseId={caseId}
				patientId={originalId}
				virtualPatientId={patientId}
				onNewDiscourseRecord={() => setShowDiscourseForm(true)}
			/>
			{showDiscourseForm && (
				<DiscourseRecord
					patientId={originalId}
					virtualPatientId={patientId}
					showCreateSickLeaveButton
					onSaveSuccess={() => discourseHistoryRef.current?.refetch()}
				/>
			)}
			<div className={styles.historyWrapper}>
				<Text
					tx={'discourse.discourseHistory'}
					className={styles.title}
					firstUpper
				/>
				<DiscourseHistory
					ref={discourseHistoryRef}
					patientId={originalId}
					virtualPatientId={patientId}
					workplaceId={workplaceId}
					openCareClaimModal={handleOpenCareClaimModal}
					onEdit={handleEditFromHistory}
				/>
			</div>
			{careClaimDiscourseId && (
				<DiscourseCareClaimModal
					isOpen
					onClose={() => setCareClaimDiscourseId(null)}
					onSaveSuccess={() => discourseHistoryRef.current?.refetch()}
					discourseId={careClaimDiscourseId}
					patientId={patientId}
				/>
			)}
		</div>
	);
};

export default Discourse;
